import * as React from "react";
import { Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import { useUser } from "../utils/AccountStore";
import { CreateQuestionnaireScope, EntityViewModel, GetEntity, GetQuestionnaireType, Questionnaire, QuestionnaireScopeAPIModel, QuestionnaireType } from "../utils/ServiceManagerAPI";
import SkeletonWrapper from "../components/SkeltonWrapper";
import StyledModal from "../components/StyledModel";
import { useEntityAction } from "../components/EntityActionContext";
import { ScopeTagsGroup } from "../components/ScopeTags";
import EntityForms from "./EntityForms";
import Questions from "./Questions";
import QuestionnaireAccessControl from "./QuestionnaireAccessControl";
import QuestionnaireAccessPoliciesCreate from "../components/QuestionnaireAccessPoliciesCreate";
import QuestionnaireAccessPoliciesUpdate from "../components/QuestionnaireAccessPoliciesUpdate";

export const QuestionnaireContext = React.createContext<Questionnaire>({} as Questionnaire);

interface EntityProps {
    onUpdate: () => void;
}

let Entity = ({ onUpdate }: EntityProps) => {
    const { entityId } = useParams<{ entityId: string }>();
    let location = useLocation();
    let [loading, setLoading] = React.useState(true);
    let { user } = useUser();
    let navigate = useNavigate();

    const [currentEntity, setCurrentEntity] = React.useState<EntityViewModel | null>(null);
    const [entityQuestionnaireType, setEntityQuestionnaireType] = React.useState<QuestionnaireType | null>(null);
    const [showSetScopeModal, setShowSetScopeModal] = React.useState(false);

    React.useEffect(() => {
        if (entityId) {
            refreshEntity(entityId);
        }
    }, [entityId]);

    React.useEffect(() => {
        if (currentEntity && currentEntity.questionnaire.questionnaireScopes.length === 0) {
            setShowSetScopeModal(true);
        }
    }, [entityQuestionnaireType, currentEntity]);

    const refreshEntity = (id: string) => {
        return Promise.all([
            GetEntity(id).then((res) => {
                setCurrentEntity(res.value);
                GetQuestionnaireType(res.value.questionnaire.questionnaireTypeID).then((res) => {
                    setEntityQuestionnaireType(res);
                });
            }),
        ]).then(() => {
            setLoading(false);
        });
    };

    const { setEntity, actions } = useEntityAction();

    const scopeGroups = entityQuestionnaireType?.scopeTypes?.[0]?.scopeType?.scopeGroups || [];

    return (
        <div className="flex flex-col w-full h-full">
            <SkeletonWrapper loading={loading}>
                <>
                    <div className="flex flex-col gap-2 pb-2">
                        <h3 className="text-xl font-light m-0">{currentEntity?.name}</h3>
                        {currentEntity && (
                            <div className="flex flex-col gap-2">
                                <ScopeTagsGroup questionnaire={currentEntity.questionnaire} />
                                <div className="flex flex-row gap-2 flex-wrap mb-2">
                                    <button
                                        className={`px-4 py-2 rounded-md font-normal mb-2 transition-colors ${
                                            location.pathname === `/entities/${currentEntity?.id}`
                                                ? "bg-brand text-white"
                                                : "bg-background-front text-text hover:bg-background-back border border-border"
                                        }`}
                                        onClick={() => navigate(`/entities/${currentEntity.id}`)}
                                    >
                                        Dashboard
                                    </button>
                                    <button
                                        className={`px-4 py-2 rounded-md font-normal mb-2 transition-colors ${
                                            location.pathname === `/entities/${currentEntity.id}/questionnaires/${currentEntity.id}`
                                                ? "bg-brand text-white"
                                                : "bg-background-front text-text hover:bg-background-back border border-border"
                                        }`}
                                        onClick={() => navigate(`/entities/${currentEntity.id}/questionnaires/${currentEntity.id}`)}
                                    >
                                        Profile
                                    </button>
                                    {user.hasQuestionnaireAccess(currentEntity.questionnaire, 64) && (
                                        <button
                                            className={`px-4 py-2 rounded-md font-normal mb-2 transition-colors ${
                                                location.pathname === `/entities/${currentEntity.id}/questionnaires/${currentEntity.id}/access`
                                                    ? "bg-brand text-white"
                                                    : "bg-background-front text-text hover:bg-background-back border border-border"
                                            }`}
                                            onClick={() => navigate(`/entities/${currentEntity.id}/questionnaires/${currentEntity.id}/access`)}
                                        >
                                            Share
                                        </button>
                                    )}
                                    {actions.map((action, idx) => (
                                        <span key={idx}>{action}</span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <Routes>
                        <Route path="/" element={<EntityForms />} />
                        <Route path="questionnaires/:questionnaireId" element={<Questions onChange={() => refreshEntity(currentEntity?.id || "")} />} />
                        <Route path="questionnaires/:questionnaireId/manifest" element={<div>Manifest</div>} />
                        <Route path="questionnaires/:questionnaireId/access" element={<QuestionnaireAccessControl />} />
                        <Route path="questionnaires/:questionnaireId/access/$create" element={<QuestionnaireAccessPoliciesCreate />} />
                        <Route path="questionnaires/:questionnaireId/access/$update/:rule" element={<QuestionnaireAccessPoliciesUpdate />} />
                    </Routes>

                    <StyledModal isOpen={showSetScopeModal} setIsOpen={setShowSetScopeModal}>
                        <div className="flex flex-col gap-2">
                            <p className="text-text">Select a group before continuing</p>
                            <div className="border border-border rounded-md overflow-hidden">
                                {scopeGroups.map((item: any) => (
                                    <div
                                        key={item.id}
                                        className="flex items-center justify-between p-3 border-b border-border last:border-b-0 hover:bg-background-back transition-colors"
                                    >
                                        <span className="text-text">{item.name}</span>
                                        <button
                                            className="px-3 py-1 bg-brand text-white rounded-md hover:bg-brand-dark transition-colors text-sm"
                                            onClick={() => {
                                                if (currentEntity) {
                                                    CreateQuestionnaireScope(currentEntity?.questionnaire.id, { scopeGroupID: item.id } as QuestionnaireScopeAPIModel).then(() => {
                                                        refreshEntity(currentEntity.id);
                                                        onUpdate();
                                                        setShowSetScopeModal(false);
                                                    });
                                                }
                                            }}
                                        >
                                            Select
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </StyledModal>
                </>
            </SkeletonWrapper>
        </div>
    );
};

export default Entity;
